import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  BookOpen,
  DollarSign,
  UserCircle,
  Menu,
  User,
  ChevronRight,
  LogOut,
  KeyRound,
  Handshake
} from 'lucide-react';


const navItems = [
  {
    label: 'Turmas',
    icon: BookOpen,
    path: '/parceiro/turmas'
  },
  {
    label: 'Financeiro',
    icon: DollarSign,
    path: '/parceiro/financeiro'
  },
  {
    label: 'Meu Perfil',
    icon: UserCircle,
    path: '/parceiro/perfil'
  }
];


function Sidebar({ onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-white">

      <div className="px-6 py-6 border-b border-slate-200">

        <div className="flex items-center gap-3">

          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
            <Handshake className="w-5 h-5 text-white" />
          </div>

          <div>
            <h1 className="text-lg font-bold text-slate-950 leading-none">
              SportManager
            </h1>

            <p className="text-xs text-slate-500 mt-1">
              Portal do Parceiro
            </p>
          </div>

        </div>

      </div>


      <nav className="flex-1 px-3 py-5 space-y-1">

        <p className="px-3 mb-3 text-[10px] font-bold tracking-[0.18em] text-slate-400 uppercase">
          Acompanhamento
        </p>

        {navItems.map((item) => (

          <NavLink
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-3 rounded-xl',
                'text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
              ].join(' ')
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            <span>{item.label}</span>
          </NavLink>

        ))}

      </nav>


      <div className="px-4 py-4 border-t border-slate-200">

        <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">

          <p className="text-xs font-semibold text-slate-700">
            Área exclusiva
          </p>

          <p className="text-[11px] leading-relaxed text-slate-500 mt-1">
            Consulte somente as turmas e informações vinculadas à sua parceria.
          </p>

        </div>

      </div>

    </div>
  );
}


export default function ParceiroLayout() {

  const { user, logout } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);


  const currentPage =
    navItems.find(item => location.pathname === item.path)?.label ||
    (location.pathname.includes('alterar-senha')
      ? 'Alterar senha'
      : 'Portal do Parceiro');


  const handleLogout = () => {
    logout();
  };


  return (

    <div
      className="flex h-screen bg-slate-50 text-slate-950"
      data-testid="parceiro-layout"
    >

      {/* SIDEBAR DESKTOP */}

      <aside className="hidden lg:flex lg:w-[260px] flex-col bg-white border-r border-slate-200 shrink-0">

        <Sidebar onNavigate={() => {}} />

      </aside>


      {/* SIDEBAR MOBILE */}

      <Sheet
        open={mobileOpen}
        onOpenChange={setMobileOpen}
      >

        <SheetContent
          side="left"
          className="w-[260px] p-0 bg-white border-slate-200"
        >

          <Sidebar
            onNavigate={() => setMobileOpen(false)}
          />

        </SheetContent>

      </Sheet>


      {/* CONTEUDO */}

      <div className="flex-1 flex flex-col min-w-0">


        {/* HEADER */}

        <header className="h-16 flex items-center justify-between px-4 lg:px-7 bg-white border-b border-slate-200 shrink-0">

          <div className="flex items-center gap-3">

            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-600 hover:text-slate-950 hover:bg-slate-100"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>


            <div className="flex items-center gap-2 text-sm">

              <span className="hidden sm:inline text-slate-400">
                Parceiro
              </span>

              <ChevronRight className="hidden sm:inline w-3 h-3 text-slate-300" />

              <span className="font-semibold text-slate-900">
                {currentPage}
              </span>

            </div>

          </div>


          {/* USUARIO */}

          <DropdownMenu>

            <DropdownMenuTrigger asChild>

              <Button
                variant="ghost"
                className="h-10 gap-2 px-2 text-slate-600 hover:text-slate-950 hover:bg-slate-100"
              >

                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">

                  {user?.avatar_url ? (

                    <img
                      src={user.avatar_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />

                  ) : (

                    <User className="w-4 h-4 text-slate-600" />

                  )}

                </div>

                <span className="hidden sm:inline text-sm font-medium max-w-[180px] truncate">
                  {user?.name}
                </span>

              </Button>

            </DropdownMenuTrigger>


            <DropdownMenuContent
              align="end"
              className="w-60 bg-white border-slate-200 text-slate-900 shadow-lg"
            >

              <div className="px-3 py-2">

                <p className="text-sm font-semibold text-slate-900 truncate">
                  {user?.name}
                </p>

                <p className="text-xs text-slate-500 truncate">
                  {user?.email}
                </p>

                <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                  Parceiro
                </p>

              </div>


              <DropdownMenuSeparator className="bg-slate-200" />


              <DropdownMenuItem
                onClick={() => navigate('/parceiro/perfil')}
                className="cursor-pointer focus:bg-slate-100"
              >
                <UserCircle className="w-4 h-4 mr-2" />
                Meu perfil
              </DropdownMenuItem>


              <DropdownMenuItem
                onClick={() => navigate('/parceiro/alterar-senha')}
                className="cursor-pointer focus:bg-slate-100"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Alterar senha
              </DropdownMenuItem>


              <DropdownMenuSeparator className="bg-slate-200" />


              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>

            </DropdownMenuContent>

          </DropdownMenu>

        </header>


        {/* PAGE */}

        <main className="flex-1 overflow-auto bg-slate-50">

          <div className="w-full max-w-[1500px] mx-auto p-4 sm:p-6 lg:p-8">

            <Outlet />

          </div>

        </main>


      </div>

    </div>
  );
}
